import { motion } from "motion/react";
import { Tabs as TabsPrimitive } from "radix-ui";
import type * as React from "react";

import { createContextFactory } from "@/utils/common";
import { cn } from "@/utils/ui";
import { useControllableValue } from "ahooks";
import { useId } from "react";

const [TabsContextProvider, useTabsContext] = createContextFactory<{
  id: string;
  selectedTab: string;
  flavor: "accenta" | "graya";
  activeStyle: "bg" | "border";
}>();

function Tabs({
  className,
  flavor = "accenta",
  activeStyle = "bg",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root> & {
  flavor?: "accenta" | "graya";
  activeStyle?: "bg" | "border";
}) {
  const id = useId();

  const [state, setState] = useControllableValue<string>(props, {
    trigger: "onValueChange",
  });

  return (
    <TabsContextProvider
      value={{ id, selectedTab: state, flavor, activeStyle }}
    >
      <TabsPrimitive.Root
        data-slot="tabs"
        className={cn("flex flex-col", className)}
        {...props}
        value={state}
        onValueChange={setState}
        data-flavor={flavor}
      />
    </TabsContextProvider>
  );
}

function TabsList({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        "text-gray-fg-secondary inline-flex h-10 items-center justify-center gap-2 isolate",
        className,
      )}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const { id, selectedTab, flavor, activeStyle } = useTabsContext();

  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "relative inline-flex items-center justify-center gap-2 px-4 py-2 whitespace-nowrap transition-[color] focus-visible:ring-4 focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 aria-invalid:focus-visible:ring-0 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      data-flavor={flavor}
      {...props}
    >
      {props.children}
      {activeStyle === "bg" && selectedTab === props.value ? (
        <motion.span
          layoutId={`tab-background-${id}`}
          className={cn(
            "absolute inset-0 z-[-1]",
            "bg-flavor-subtle-bg",
            "ring-gray-subtle-bg/20 outline-gray-subtle-bg/40",
          )}
        />
      ) : null}
      {activeStyle === "border" && selectedTab === props.value ? (
        <motion.span
          layoutId={`tab-border-${id}`}
          className={cn(
            "absolute inset-0 z-[-1]",
            "border-b-2 border-flavor-solid",
            "ring-gray-subtle-bg/20 outline-gray-subtle-bg/40",
          )}
        />
      ) : null}
    </TabsPrimitive.Trigger>
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn(
        "ring-gray-border-subtle/20 outline-gray-border-subtle/40 flex-1 transition-[color,box-shadow] focus-visible:ring-4 focus-visible:outline-1 aria-invalid:focus-visible:ring-0",
        className,
      )}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
